// Bridge between Supabase Auth and the local user store used across the app.
// Phase 1 of the production migration keeps the local store as the app-wide
// profile cache while making Supabase the true source for authentication.

import { supabase } from '@/integrations/supabase/client';
import type { User } from '@supabase/supabase-js';
import { addUser, getUserById, updateUserProfile, type UserProfile } from '@/store/users';
import type { UserRole, UserPlan } from '@/types/user';

const initialsFrom = (name: string): string =>
  name
    .trim()
    .split(/\s+/)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('')
    .slice(0, 2) || '?';

/**
 * Fetch the Supabase profile row for the given auth user and mirror it into
 * the local user store. Creates the local record if it does not exist yet.
 * Safe to call multiple times.
 */
export async function hydrateProfileFromAuth(user: User): Promise<void> {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select(
        'id, username, name, first_name, last_name, role, plan, bio, location, avatar_url, verified, banned, profile_completed, profile_built, created_at, updated_at',
      )
      .eq('id', user.id)
      .maybeSingle();

    if (error) {
      console.warn('[Auth] profiles fetch failed', error);
    }

    const meta = user.user_metadata ?? {};
    const firstName = (data?.first_name ?? meta.first_name ?? '') as string;
    const lastName = (data?.last_name ?? meta.last_name ?? '') as string;
    const name =
      (data?.name as string | undefined) ||
      `${firstName} ${lastName}`.trim() ||
      user.email?.split('@')[0] ||
      'User';
    const username =
      (data?.username as string | undefined) ||
      (name.toLowerCase().replace(/\s+/g, '') + user.id.replace(/-/g, '').slice(0, 4));
    const role = ((data?.role as UserRole | undefined) ?? 'player') as UserRole;
    const plan = ((data?.plan as UserPlan | undefined) ?? 'free') as UserPlan;

    const merged: UserProfile = {
      id: user.id,
      name,
      username,
      initials: initialsFrom(name),
      role,
      plan,
      bio: (data?.bio as string | undefined) || undefined,
      location: (data?.location as string | undefined) || undefined,
      avatarUrl: (data?.avatar_url as string | undefined) || undefined,
      verified: !!data?.verified,
      banned: !!data?.banned,
      profileCompleted: !!data?.profile_completed,
      profileBuilt: !!data?.profile_built,
      firstName: firstName || undefined,
      lastName: lastName || undefined,
      email: user.email ?? undefined,
      emailVerified: !!user.email_confirmed_at,
    };

    if (getUserById(user.id)) {
      updateUserProfile(user.id, merged);
    } else {
      addUser(merged);
    }
  } catch (err) {
    console.error('[Auth] hydrateProfileFromAuth failed', err);
  }
}

/**
 * Fire-and-forget: mirror a subset of local profile changes into Supabase so
 * they survive across devices / sign-ins.
 */
type ProfileUpdate = {
  name?: string;
  username?: string;
  first_name?: string;
  last_name?: string;
  bio?: string;
  location?: string;
  avatar_url?: string;
  role?: string;
  plan?: string;
  profile_completed?: boolean;
  profile_built?: boolean;
};

export function syncProfileToSupabase(userId: string, patch: Partial<UserProfile>): void {
  const payload: ProfileUpdate = {};
  if (patch.name !== undefined) payload.name = patch.name;
  if (patch.username !== undefined) payload.username = patch.username;
  if (patch.firstName !== undefined) payload.first_name = patch.firstName;
  if (patch.lastName !== undefined) payload.last_name = patch.lastName;
  if (patch.bio !== undefined) payload.bio = patch.bio;
  if (patch.location !== undefined) payload.location = patch.location;
  if (patch.avatarUrl !== undefined) payload.avatar_url = patch.avatarUrl;
  if (patch.role !== undefined) payload.role = patch.role;
  if (patch.plan !== undefined) payload.plan = patch.plan;
  if (patch.profileCompleted !== undefined) payload.profile_completed = patch.profileCompleted;
  if (patch.profileBuilt !== undefined) payload.profile_built = patch.profileBuilt;
  if (Object.keys(payload).length === 0) return;
  void supabase
    .from('profiles')
    .update(payload)
    .eq('id', userId)
    .then(({ error }) => {
      if (error) console.warn('[Auth] syncProfileToSupabase failed', error);
    });
}
